import React, { useState, useEffect } from "react";
import { Card, Select, Input, Button, Row, Col, Upload, message } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import OptionsInput from "./OptionsInput";

const QuestionBuilder = ({
  totalQuestions,
  setTotalQuestions,
  editingQuestion,
  onEditComplete,
}) => {
  const [questionType, setQuestionType] = useState("tf");
  const [questionInput, setQuestionInput] = useState("");
  const [editId, setEditId] = useState(null);

  // Question-specific states
  const [tfAnswer, setTfAnswer] = useState(null);
  const [option1, setOption1] = useState("");
  const [option2, setOption2] = useState("");
  const [option3, setOption3] = useState("");
  const [option4, setOption4] = useState("");
  const [correctOption, setCorrectOption] = useState(null);
  const [correctOptions, setCorrectOptions] = useState([]);
  const [blankAnswers, setBlankAnswers] = useState("");
  const [matchingPairs, setMatchingPairs] = useState([
    { number: 1, label: "A", left: "", right: "" },
  ]);
  const [tableRows, setTableRows] = useState([
    { number: "1", label: "A", text: "", answer: "" },
  ]);
  const [imageUrls, setImageUrls] = useState([]);
  const [imageAnswer, setImageAnswer] = useState("");
  const [fbdAnswer, setFbdAnswer] = useState("");
  const [fbdDescription, setFbdDescription] = useState("");
  const [mlAnswer, setMlAnswer] = useState("");
  const [tqAnswer, setTqAnswer] = useState("");

  const questionTypeOptions = [
    { value: "tf", label: "True or False" },
    { value: "mcq", label: "MCQ (Single Answer)" },
    { value: "multiple_answer", label: "Multiple Choice (Multiple Answers)" },
    { value: "fill_blank", label: "Fill in the Blanks" },
    { value: "fill_blank_desc", label: "Fill in the Blanks with Description" },
    { value: "matching_list", label: "Match the Following" },
    { value: "table_q", label: "Table Questions" },
    { value: "image", label: "Image-based Questions" },
  ];

  // Load question data when editing
  useEffect(() => {
    if (editingQuestion) {
      loadQuestionForEdit(editingQuestion);
    }
  }, [editingQuestion]);

  const addMatchingRow = () => {
    const nextCharCode =
      matchingPairs.length > 0
        ? matchingPairs[matchingPairs.length - 1].label.charCodeAt(0) + 1
        : "A".charCodeAt(0);
    const nextLabel = String.fromCharCode(nextCharCode);

    const indexAfterUpdation =
      matchingPairs.length > 0
        ? matchingPairs[matchingPairs.length - 1].number + 1
        : 1;
    setMatchingPairs([
      ...matchingPairs,
      { label: nextLabel, number: indexAfterUpdation, left: "", right: "" },
    ]);
  };

  const updateMatchingPair = (index, label, value) => {
    const updated = [...matchingPairs];
    updated[index][label] = value;
    setMatchingPairs(updated);
  };

  const removeMatchingRow = (index) => {
    const updated = matchingPairs.filter((_, i) => i !== index);
    setMatchingPairs(
      updated.length > 0
        ? updated
        : [{ label: "A", number: 1, left: "", right: "" }],
    );
  };

  const addTableRow = () => {
    const nextCharCode =
      tableRows.length > 0
        ? tableRows[tableRows.length - 1].label.charCodeAt(0) + 1
        : "A".charCodeAt(0);
    const nextLabel = String.fromCharCode(nextCharCode);
    setTableRows([...tableRows, { label: nextLabel, text: "", answer: "" }]);
  };

  const updateTableRow = (index, field, value) => {
    const updated = [...tableRows];
    updated[index][field] = value;
    setTableRows(updated);
  };

  const removeTableRow = (index) => {
    const updated = tableRows.filter((_, i) => i !== index);
    setTableRows(
      updated.length > 0 ? updated : [{ label: "A", text: "", answer: "" }],
    );
  };

  const handleImageFileChange = async (info) => {
    const file = info.file || info.file.originFileObj;
    if (!file) return;

    // Validate file type
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return;
    }

    // Validate file size (max 5MB)
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB!");
      return;
    }

    try {
      message.loading(`Processing ${file.name}...`, 0);

      // Convert file to Base64
      const base64String = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });

      message.destroy(); // Remove loading message

      // Check if already added (check by filename)
      const isDuplicate = imageUrls.some((img) => img.filename === file.name);
      if (isDuplicate) {
        message.warning(`Image "${file.name}" is already added`);
        return;
      }

      // Store image data with base64
      const imageData = {
        filename: file.name,
        base64: base64String, // Full data URL: "data:image/png;base64,iVBORw0KG..."
        contentType: file.type,
        size: file.size,
      };

      setImageUrls((prev) => [...prev, imageData]);
      message.success(`Image "${file.name}" added successfully!`);
    } catch (error) {
      message.destroy();
      console.error("Error processing image:", error);
      message.error(`Failed to process ${file.name}. Please try again.`);
    }
  };
  const resetForm = () => {
    setQuestionInput("");
    setQuestionType("tf");
    setEditId(null);
    setTfAnswer(null);
    setOption1("");
    setOption2("");
    setOption3("");
    setOption4("");
    setCorrectOption(null);
    setCorrectOptions([]);
    setBlankAnswers("");
    setMatchingPairs([{ label: "A", number: 1, left: "", right: "" }]);
    setTableRows([{ label: "A", number: 1, text: "", answer: "" }]);
    setMlAnswer("");
    setTqAnswer("");
    setImageUrls([]);
    setImageAnswer("");
    setFbdAnswer("");
    setFbdDescription("");

    //clear editing state in parent
    if (onEditComplete) {
      onEditComplete();
    }
  };
  // Helper function to load question data into form
  const loadQuestionForEdit = (question) => {
    setEditId(question.id);
    setQuestionType(question.type);
    setQuestionInput(question.question);

    // Load type-specific data
    switch (question.type) {
      case "tf":
        setTfAnswer(question.answer);
        break;

      case "mcq":
        setOption1(question.options[0] || "");
        setOption2(question.options[1] || "");
        setOption3(question.options[2] || "");
        setOption4(question.options[3] || "");
        setCorrectOption(question.correctAnswers[0] || null);
        break;

      case "multiple_answer":
        setOption1(question.options[0] || "");
        setOption2(question.options[1] || "");
        setOption3(question.options[2] || "");
        setOption4(question.options[3] || "");
        setCorrectOptions(question.correctAnswers || []);
        break;

      case "fill_blank":
        setBlankAnswers(question.blanks.join(", "));
        break;

      case "fill_blank_desc":
        setOption1(question.options[0] || "");
        setOption2(question.options[1] || "");
        setOption3(question.options[2] || "");
        setOption4(question.options[3] || "");
        setFbdAnswer(question.fbdAnswer || "");
        setFbdDescription(question.fbdDescription || "");
        break;

      case "matching_list":
        setOption1(question.options[0] || "");
        setOption2(question.options[1] || "");
        setOption3(question.options[2] || "");
        setOption4(question.options[3] || "");
        setMlAnswer(question.answer || "");
        setMatchingPairs(question.pairs || [{ left: "", right: "" }]);
        break;

      case "table_q":
        setOption1(question.options[0] || "");
        setOption2(question.options[1] || "");
        setOption3(question.options[2] || "");
        setOption4(question.options[3] || "");
        setTqAnswer(question.answer || "");
        setCorrectOption(question.answer || null);
        setTableRows(
          question.table_rows || [{ label: "A", text: "", answer: "" }],
        );
        break;

      case "image":
        setOption1(question.options[0] || "");
        setOption2(question.options[1] || "");
        setOption3(question.options[2] || "");
        setOption4(question.options[3] || "");
        setImageUrls(question.imageData || []);
        setImageAnswer(question.answer || "");
        break;
    }

    // Optional: Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAddQuestion = () => {
    if (!questionInput.trim()) {
      message.warning("Please enter a question");
      return;
    }

    let questionData = {
      id:
        editId !== null
          ? editId
          : totalQuestions.length > 0
            ? totalQuestions[totalQuestions.length - 1].id + 1
            : 1,
      type: questionType,
      question: questionInput,
    };

    // Add type-specific data
    switch (questionType) {
      case "tf":
        if (tfAnswer === null) {
          message.warning("Please select True or False answer");
          return;
        }
        questionData.answer = tfAnswer;
        break;

      case "mcq":
        const mcqOpts = [option1, option2, option3, option4].filter((o) =>
          o.trim(),
        );
        if (mcqOpts.length < 2) {
          message.warning("Please provide at least 2 options");
          return;
        }
        if (!correctOption) {
          message.warning("Please select the correct option");
          return;
        }
        questionData.options = mcqOpts;
        questionData.correctAnswers = [correctOption];
        break;

      case "multiple_answer":
        const multiOpts = [option1, option2, option3, option4].filter((o) =>
          o.trim(),
        );
        if (multiOpts.length < 2) {
          message.warning("Please provide at least 2 options");
          return;
        }
        if (correctOptions.length === 0) {
          message.warning("Please select at least one correct option");
          return;
        }
        questionData.options = multiOpts;
        questionData.correctAnswers = correctOptions;
        break;

      case "fill_blank":
        const blanks = blankAnswers
          .split(",")
          .map((b) => b.trim())
          .filter((b) => b);
        if (blanks.length === 0) {
          message.warning("Please provide answers for the blanks");
          return;
        }
        questionData.blanks = blanks;
        break;

      case "fill_blank_desc":
        // Validation: At least 2 options required
        const fbdOpts = [option1, option2, option3, option4].filter((o) =>
          o.trim(),
        );
        if (fbdOpts.length < 2) {
          message.warning("Please provide at least 2 options");
          return;
        }

        // Validation: Answer is required
        if (!fbdAnswer.trim()) {
          message.warning("Please enter the correct answer");
          return;
        }

        if (fbdDescription.length <= 0) {
          message.warning("Please enter the description");
          return;
        }

        // Store all data in questionData
        questionData.options = fbdOpts; // Store as array like MCQ
        questionData.fbdAnswer = fbdAnswer.trim();
        questionData.fbdDescription = fbdDescription.trim();
        break;

      case "matching_list":
        const mlOpts = [option1, option2, option3, option4].filter((o) =>
          o.trim(),
        );
        const pairs = matchingPairs.filter(
          (p) => p.left.trim() && p.right.trim(),
        );
        if (pairs.length === 0) {
          message.warning("Please add at least one matching pair");
          return;
        }
        if (mlOpts.length < 2) {
          message.warning("Please provide at least 2 options");
          return;
        }
        if (!mlAnswer.trim()) {
          message.warning("Please select the correct answer");
          return;
        }

        questionData.options = mlOpts;
        questionData.pairs = pairs;
        questionData.answer = mlAnswer;
        break;

      case "table_q":
        const tqOpts = [option1, option2, option3, option4].filter((o) =>
          o.trim(),
        );
        if (tqOpts.length < 2) {
          message.warning("Please provide at least 2 options");
          return;
        }
        if (!tqAnswer || !tqAnswer.trim()) {
          message.warning("Please select the correct answer");
          return;
        }
        const rows = tableRows.filter((r) => r.text.trim() && r.answer.trim());
        if (rows.length === 0) {
          message.warning("Please add at least one table row");
          return;
        }
        questionData.options = tqOpts;
        questionData.table_rows = rows;
        questionData.answer = tqAnswer;
        break;

      case "image":
        const imgOpts = [option1, option2, option3, option4].filter((o) =>
          o.trim(),
        );
        if (imgOpts.length < 2) {
          message.warning("Please provide at least 2 options");
          return;
        }
        if (imageUrls.length === 0) {
          message.warning("Please select at least one image");
          return;
        }
        if (!imageAnswer.trim()) {
          message.warning("Please provide an answer for the image question");
          return;
        }
        questionData.options = imgOpts;
        questionData.imageData = imageUrls;
        questionData.answer = imageAnswer;
        break;
    }

    if (editId !== null) {
      setTotalQuestions(
        totalQuestions.map((q) => (q.id === editId ? questionData : q)),
      );
      message.success("Question updated successfully!");
    } else {
      setTotalQuestions([...totalQuestions, questionData]);
      message.success("Question added successfully!");
    }

    resetForm();
  };

  return (
    <Card
      className="questionBuilderCard"
      bordered={false}
      style={{ marginTop: 16 }}
    >
      {/* UI when the edit is in process */}
      {editId !== null && (
        <div
          style={{
            padding: "12px 16px",
            background: "#fff7e6",
            border: "1px solid #ffd666",
            borderRadius: "4px",
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ color: "#d46b08", fontWeight: 500 }}>
            ✏️ Editing Question #{editId}
          </span>
          <Button
            size="small"
            onClick={() => {
              resetForm();
              message.info("Edit cancelled");
            }}
          >
            Cancel
          </Button>
        </div>
      )}
      <p className="fieldLabel">Question Type</p>
      <Select
        value={questionType}
        onChange={setQuestionType}
        options={questionTypeOptions}
        className="formInput"
        style={{ width: "100%" }}
      />

      <p className="fieldLabel">Question</p>
      <TextArea
        rows={3}
        placeholder={
          questionType === "fill_blank" || questionType === "fill_blank_desc"
            ? "Enter your question here (use ____ for blanks)..."
            : "Enter your question here..."
        }
        value={questionInput}
        onChange={(e) => setQuestionInput(e.target.value)}
        className="formInput"
      />

      {/* True/False Fields */}
      {questionType === "tf" && (
        <>
          <p className="fieldLabel">Correct Answer</p>
          <Select
            value={tfAnswer}
            onChange={setTfAnswer}
            className="formInput"
            placeholder="Select True or False"
            style={{ width: "100%" }}
            options={[
              { value: true, label: "True" },
              { value: false, label: "False" },
            ]}
          />
        </>
      )}

      {/* MCQ / Multiple Answer Fields */}
      {(questionType === "mcq" || questionType === "multiple_answer") && (
        <OptionsInput
          option1={option1}
          option2={option2}
          option3={option3}
          option4={option4}
          setOption1={setOption1}
          setOption2={setOption2}
          setOption3={setOption3}
          setOption4={setOption4}
          showAnswerSelect={true}
          answerSelectMode={
            questionType === "multiple_answer" ? "multiple" : "single"
          }
          answerSelectLabel={
            questionType === "multiple_answer"
              ? "Correct Options (select multiple)"
              : "Correct Option"
          }
          answerSelectPlaceholder={
            questionType === "multiple_answer"
              ? "Select all correct answers"
              : "Select the correct answer"
          }
          answerValue={
            questionType === "multiple_answer" ? correctOptions : correctOption
          }
          setAnswerValue={
            questionType === "multiple_answer"
              ? setCorrectOptions
              : setCorrectOption
          }
          required={true}
        />
      )}
      {/* fill in the blanks insert blanks buttons */}
      {(questionType === "fill_blank" ||
        questionType === "fill_blank_desc") && (
        <Button
          size="small"
          style={{ marginTop: 8, marginBottom: 8 }}
          onClick={() =>
            setQuestionInput((prev) => (prev ? `${prev} ____ ` : "____ "))
          }
        >
          Insert Blank (____)
        </Button>
      )}

      {/* Fill in the Blanks Fields */}
      {questionType === "fill_blank" && (
        <>
          <p className="fieldLabel">Answers for Blanks</p>
          <TextArea
            rows={2}
            placeholder="Enter answers separated by commas (e.g., answer1, answer2, answer3)"
            value={blankAnswers}
            onChange={(e) => setBlankAnswers(e.target.value)}
            className="formInput"
          />
          <p style={{ fontSize: "11px", color: "#888", marginTop: 4 }}>
            Provide answers in the order they appear in the question
          </p>
        </>
      )}

      {/* Options Input */}
      {questionType === "fill_blank_desc" && (
        <>
          <OptionsInput
            option1={option1}
            option2={option2}
            option3={option3}
            option4={option4}
            setOption1={setOption1}
            setOption2={setOption2}
            setOption3={setOption3}
            setOption4={setOption4}
            showOptionsLabel={true}
            showHint={true}
            hintText='For multiple alternatives, separate with commas (e.g., "Answer 1, Answer 2")'
            showAnswerSelect={true}
            answerSelectMode="single"
            answerSelectLabel="Correct Answer"
            answerSelectPlaceholder="Select the correct answer"
            answerValue={fbdAnswer}
            setAnswerValue={setFbdAnswer}
            required={true}
          />

          <p className="fieldLabel">Description</p>
          <p
            style={{
              fontSize: "11px",
              color: "#666",
              marginTop: -4,
              marginBottom: 8,
            }}
          >
            Explain why this is the correct answer
          </p>
          <TextArea
            rows={3}
            placeholder="Explain the description here..."
            value={fbdDescription}
            onChange={(e) => setFbdDescription(e.target.value)}
            className="formInput"
          />
        </>
      )}

      {/* Matching List Fields */}
      {questionType === "matching_list" && (
        <>
          <p className="fieldLabel">Matching Pairs</p>
          {matchingPairs.map((pair, index) => (
            <Row key={index} gutter={8} style={{ marginBottom: 8 }}>
              <Col span={2}>
                <Input
                  placeholder="Number"
                  value={pair.number}
                  onChange={(e) => {
                    updateMatchingPair(index, "number", e.target.value);
                  }}
                ></Input>
              </Col>
              <Col span={8}>
                <Input
                  placeholder={`Column A - Item ${index + 1}`}
                  value={pair.left}
                  onChange={(e) =>
                    updateMatchingPair(index, "left", e.target.value)
                  }
                />
              </Col>
              <Col span={2}>
                <Input
                  placeholder="Label"
                  value={pair.label}
                  onChange={(e) => {
                    updateMatchingPair(index, "label", e.target.value);
                  }}
                ></Input>
              </Col>
              <Col span={9}>
                <Input
                  placeholder={`Column B - Match ${index + 1}`}
                  value={pair.right}
                  onChange={(e) =>
                    updateMatchingPair(index, "right", e.target.value)
                  }
                />
              </Col>
              <Col span={2}>
                <Button danger onClick={() => removeMatchingRow(index)}>
                  x
                </Button>
              </Col>
            </Row>
          ))}
          <Button
            type="dashed"
            onClick={addMatchingRow}
            style={{ width: "100%" }}
          >
            + Add Pair
          </Button>

          <OptionsInput
            option1={option1}
            option2={option2}
            option3={option3}
            option4={option4}
            setOption1={setOption1}
            setOption2={setOption2}
            setOption3={setOption3}
            setOption4={setOption4}
            showOptionsLabel={true}
            required={true}
            showAnswerSelect={true}
            answerValue={mlAnswer}
            setAnswerValue={setMlAnswer}
          />
        </>
      )}

      {/* Table Questions Fields */}
      {questionType === "table_q" && (
        <>
          <p className="fieldLabel">Table Rows</p>
          {tableRows.map((row, index) => (
            <Row
              key={index}
              gutter={8}
              style={{ marginBottom: 8 }}
              align="middle"
            >
              <Col span={2}>
                <span>
                  <b>{index + 1}.</b>
                </span>
              </Col>
              <Col span={9}>
                <Input
                  placeholder="Field"
                  value={row.text}
                  onChange={(e) =>
                    updateTableRow(index, "text", e.target.value)
                  }
                />
              </Col>
              <Col span={2}>
                <Input
                  placeholder="Label (e.g., A, B)"
                  value={row.label}
                  onChange={(e) =>
                    updateTableRow(index, "label", e.target.value)
                  }
                />
              </Col>
              <Col span={9}>
                <Input
                  placeholder="Description"
                  value={row.answer}
                  onChange={(e) =>
                    updateTableRow(index, "answer", e.target.value)
                  }
                />
              </Col>
              <Col span={2}>
                <Button
                  danger
                  size="small"
                  onClick={() => removeTableRow(index)}
                >
                  X
                </Button>
              </Col>
            </Row>
          ))}
          <Button type="dashed" onClick={addTableRow} style={{ width: "100%" }}>
            + Add Row
          </Button>

          <OptionsInput
            option1={option1}
            option2={option2}
            option3={option3}
            option4={option4}
            setOption1={setOption1}
            setOption2={setOption2}
            setOption3={setOption3}
            setOption4={setOption4}
            showOptionsLabel={true}
            required={true}
            answerValue={tqAnswer}
            setAnswerValue={setTqAnswer}
            showAnswerSelect={true}
          />
        </>
      )}

      {/* Image Question Fields */}
      {questionType === "image" && (
        <>
          <p className="fieldLabel">Upload Images</p>
          <Upload
            accept="image/*"
            multiple={true}
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleImageFileChange}
            required
          >
            <Button style={{ width: "100%" }}>Choose Image File(s)</Button>
          </Upload>
          <p style={{ fontSize: "11px", color: "#888", marginTop: 4 }}>
            Supported formats: PNG, JPG, WEBP. You can select multiple images.
            Max 5MB per image.
          </p>

          {/* Display selected images */}
          {/* Display selected images */}
          {imageUrls.length > 0 && (
            <div style={{ marginTop: 12, marginBottom: 16 }}>
              <p style={{ fontSize: "12px", fontWeight: 500, marginBottom: 8 }}>
                Selected Images ({imageUrls.length}):
              </p>
              {imageUrls.map((imageData, index) => {
                return (
                  <Row
                    key={index}
                    gutter={8}
                    style={{
                      marginBottom: 8,
                      padding: "8px 12px",
                      background: "#f5f5f5",
                      borderRadius: 4,
                    }}
                    align="middle"
                  >
                    <Col span={2}>
                      <span style={{ fontWeight: 500 }}>{index + 1}.</span>
                    </Col>
                    <Col span={2}>
                      {/* Preview thumbnail */}
                      <img
                        src={imageData.base64}
                        alt={imageData.filename}
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "cover",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                        }}
                      />
                    </Col>
                    <Col span={18}>
                      <div>
                        <div style={{ fontSize: "13px", fontWeight: 500 }}>
                          {imageData.filename}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#999",
                            marginTop: 2,
                          }}
                        >
                          {(imageData.size / 1024).toFixed(2)} KB •{" "}
                          {imageData.contentType}
                        </div>
                      </div>
                    </Col>
                    <Col span={2}>
                      <Button
                        danger
                        size="small"
                        onClick={() => {
                          setImageUrls((prev) =>
                            prev.filter((_, i) => i !== index),
                          );
                          message.success("Image removed");
                        }}
                      >
                        ×
                      </Button>
                    </Col>
                  </Row>
                );
              })}
            </div>
          )}

          <OptionsInput
            option1={option1}
            option2={option2}
            option3={option3}
            option4={option4}
            setOption1={setOption1}
            setOption2={setOption2}
            setOption3={setOption3}
            setOption4={setOption4}
            showOptionsLabel={true}
            showAnswerSelect={true}
            answerSelectMode="single"
            answerSelectLabel="Correct Answer"
            answerSelectPlaceholder="Select the correct answer"
            answerValue={imageAnswer}
            setAnswerValue={setImageAnswer}
            required={true}
          />
        </>
      )}

      <div
        style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}
      >
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          onClick={handleAddQuestion}
          size="middle"
        >
          {editId !== null ? "Update Question" : "Add Question"}
        </Button>
      </div>
    </Card>
  );
};

export default QuestionBuilder;
